import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Bell, Shield, ChevronRight, Moon, Sun, Heart, Camera } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useMembers } from '../hooks/useMembers';
import { supabase } from '../lib/supabase';
import { AppShell } from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Toggle } from '../components/ui/Toggle';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { NotificationPreferences } from '../types';
import {
  isPushSupported,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentPushSubscription,
} from '../lib/pushNotifications';

export function SettingsPage() {
  const { profile, user, signOut, refreshProfile, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { members } = useMembers();
  const navigate = useNavigate();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Profile form
  const [fullName, setFullName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [birthday, setBirthday] = useState('');
  const [anniversary, setAnniversary] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedProfile, setSavedProfile] = useState(false);

  // Spouse
  const [spouseName, setSpouseName] = useState('');
  const [spouseUserId, setSpouseUserId] = useState('');

  // Avatar
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Notification prefs
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>({
    weekly_reminder: true,
    birthday_reminder: true,
    admin_announcements: true,
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_togglingPush, setTogglingPush] = useState(false);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      setAddress(profile.address ?? '');
      setPhone(profile.phone ?? '');
      setBirthday(profile.birthday ?? '');
      setAnniversary(profile.anniversary ?? '');
      setSpouseName(profile.spouse_name ?? '');
      setSpouseUserId(profile.spouse_user_id ?? '');
      setAvatarUrl(profile.avatar_url ?? null);
      setNotifPrefs(
        profile.notification_preferences ?? {
          weekly_reminder: true,
          birthday_reminder: true,
          admin_announcements: true,
        }
      );
    }
  }, [profile]);

  useEffect(() => {
    isPushSupported().then((supported) => {
      setPushSupported(supported);
      if (supported) {
        getCurrentPushSubscription().then((sub) => {
          setPushEnabled(!!sub);
        });
      }
    });
  }, []);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setAvatarError(null);

    // Validate size (max 5 MB) and type
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Photo must be under 5 MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${user.id}/avatar.${ext}`;

      // Upload to Supabase Storage (avatars bucket must exist)
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) {
        setAvatarError('Upload failed. Please try again.');
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      const publicUrl = urlData?.publicUrl
        ? `${urlData.publicUrl}?t=${Date.now()}`
        : null;

      if (!publicUrl) {
        setAvatarError('Could not get photo URL.');
        return;
      }

      // Save to profile
      await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      setAvatarUrl(publicUrl);
      await refreshProfile();
    } finally {
      setUploadingAvatar(false);
      // Reset input so same file can be re-selected
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);

    const { error } = await supabase.from('profiles').update({
      full_name: fullName,
      address,
      phone: phone || null,
      birthday: birthday || null,
      anniversary: anniversary || null,
      spouse_name: spouseName || null,
      spouse_user_id: spouseUserId || null,
    }).eq('id', user.id);

    // If a spouse user was linked, link them back
    if (!error && spouseUserId && spouseUserId !== profile?.spouse_user_id) {
      await supabase.from('profiles').update({
        spouse_user_id: user.id,
        spouse_name: fullName.split(' ')[0],
      }).eq('id', spouseUserId);
    }

    setSaving(false);
    if (!error) {
      await refreshProfile();
      setSavedProfile(true);
      setTimeout(() => setSavedProfile(false), 3000);
    }
  };

  const handleNotifToggle = async (
    key: keyof NotificationPreferences,
    val: boolean
  ) => {
    const updated = { ...notifPrefs, [key]: val };
    setNotifPrefs(updated);
    if (user) {
      await supabase
        .from('profiles')
        .update({ notification_preferences: updated })
        .eq('id', user.id);
    }
  };

  const handlePushToggle = async (enabled: boolean) => {
    if (!user) return;
    setTogglingPush(true);
    if (enabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        const success = await subscribeToPush(user.id);
        setPushEnabled(success);
      }
    } else {
      await unsubscribeFromPush(user.id);
      setPushEnabled(false);
    }
    setTogglingPush(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const otherMembers = members.filter((m) => m.id !== user?.id);

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  if (!profile) return <AppShell><LoadingSpinner /></AppShell>;

  return (
    <AppShell>
      <PageHeader title="Settings" />

      <div className="px-4 pt-3 space-y-4 pb-6">

        {/* Profile Section — photo + all profile fields + save in one card */}
        <Card>
          <CardHeader
            title="My Profile"
            subtitle="Your info shared with the group"
          />

          {/* Avatar upload */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Your profile"
                  className="w-20 h-20 rounded-full object-cover border-2 border-warm-100 dark:border-gray-700"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-warm-100 dark:bg-warm-900 flex items-center justify-center border-2 border-warm-100 dark:border-gray-700">
                  <span className="text-2xl font-bold text-warm-600 dark:text-warm-300">{initials}</span>
                </div>
              )}
              {/* Camera badge overlay */}
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-warm-500 hover:bg-warm-600 text-white flex items-center justify-center shadow-md active:scale-95 transition-all"
                aria-label="Change photo"
              >
                {uploadingAvatar ? (
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera size={13} />
                )}
              </button>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {profile.full_name}
              </p>
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="text-xs text-warm-600 dark:text-warm-400 hover:text-warm-700 dark:hover:text-warm-300 mt-0.5"
              >
                {uploadingAvatar ? 'Uploading…' : avatarUrl ? 'Change photo' : 'Add a photo'}
              </button>
              {avatarError && (
                <p className="text-xs text-red-500 mt-1">{avatarError}</p>
              )}
            </div>

            {/* Hidden file input */}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>

          <div className="space-y-3">
            <Input
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
            />
            <Input
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, City, State"
            />
            <Input
              label="Phone (optional)"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 000-0000"
            />
            <Input
              label="Birthday (optional)"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
            />
            <Input
              label="Anniversary (optional)"
              type="date"
              value={anniversary}
              onChange={(e) => setAnniversary(e.target.value)}
            />

            {/* Spouse section — inline, no separate card */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <Heart size={14} className="text-rose-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Spouse / Partner
                </span>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Link to a member (if they have an account)
                  </label>
                  <select
                    value={spouseUserId}
                    onChange={(e) => {
                      setSpouseUserId(e.target.value);
                      if (e.target.value) {
                        const selected = otherMembers.find((m) => m.id === e.target.value);
                        if (selected) setSpouseName(selected.full_name.split(' ')[0]);
                      }
                    }}
                    className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100
                      focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent transition-all"
                  >
                    <option value="">No linked member</option>
                    {otherMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Or enter name manually"
                  value={spouseName}
                  onChange={(e) => setSpouseName(e.target.value)}
                  placeholder="e.g. Sarah"
                />

                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Used on the calendar ("Leslie &amp; Sarah's Anniversary") and when hosting.
                </p>
              </div>
            </div>

            {savedProfile && (
              <div className="rounded-xl bg-green-50 dark:bg-green-950 border border-green-100 dark:border-green-900 px-3 py-2 text-sm text-green-700 dark:text-green-300">
                ✓ Profile saved!
              </div>
            )}

            {/* Single Save button for entire profile section */}
            <Button
              onClick={handleSaveProfile}
              loading={saving}
              size="sm"
              fullWidth
            >
              Save Profile
            </Button>
          </div>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader
            title="Appearance"
            action={theme === 'dark'
              ? <Moon size={18} className="text-warm-400" />
              : <Sun size={18} className="text-warm-500" />
            }
          />

          <Toggle
            label="Dark Mode"
            description="Switch to a darker color scheme"
            checked={theme === 'dark'}
            onChange={toggleTheme}
          />
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader
            title="Notifications"
            action={<Bell size={18} className="text-warm-500" />}
          />

          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {pushSupported && (
              <Toggle
                label="Push Notifications"
                description="Enable browser push alerts"
                checked={pushEnabled}
                onChange={handlePushToggle}
              />
            )}

            {!pushSupported && (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-2">
                Push notifications are not supported in this browser.
              </p>
            )}

            <Toggle
              label="Weekly Reminders"
              description="Get reminded about Saturday lunch"
              checked={notifPrefs.weekly_reminder}
              onChange={(val) => handleNotifToggle('weekly_reminder', val)}
            />

            <Toggle
              label="Birthday Reminders"
              description="Celebrate members' birthdays"
              checked={notifPrefs.birthday_reminder}
              onChange={(val) => handleNotifToggle('birthday_reminder', val)}
            />

            <Toggle
              label="Admin Announcements"
              description="Important messages from your group admin"
              checked={notifPrefs.admin_announcements}
              onChange={(val) => handleNotifToggle('admin_announcements', val)}
            />
          </div>
        </Card>

        {/* Admin Section */}
        {isAdmin && (
          <Card>
            <CardHeader
              title="Admin"
              subtitle="You have admin access"
              action={<Shield size={18} className="text-warm-500" />}
            />

            <div className="space-y-2">
              <AdminLink
                label="Create Event"
                onClick={() => navigate('/admin/create-event')}
              />
              <AdminLink
                label="Send Announcement"
                onClick={() => navigate('/admin/announcement')}
              />
            </div>
          </Card>
        )}

        {/* Account */}
        <Card>
          <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Signed in as{' '}
            <span className="font-medium text-gray-600 dark:text-gray-400">
              {user?.email}
            </span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="text-red-500 hover:bg-red-50 dark:hover:bg-red-950 w-full justify-center"
          >
            <LogOut size={15} className="mr-2" />
            Sign Out
          </Button>
        </Card>

        <p className="text-center text-xs text-gray-300 dark:text-gray-600">
          Sabbath Lunch · Private Group App
        </p>
      </div>
    </AppShell>
  );
}

function AdminLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-warm-50 dark:hover:bg-gray-800 transition-colors text-sm text-gray-700 dark:text-gray-300 font-medium"
    >
      {label}
      <ChevronRight size={15} className="text-gray-400 dark:text-gray-500" />
    </button>
  );
}
